import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import WalletConnectButton from './WalletConnectButton';
import GoogleSignInButton from './GoogleSignInButton';
import AuthTurnstileSection from './AuthTurnstileSection';
import { useTurnstile } from '../hooks/useTurnstile';
import { useNotification } from './Notification';
import Modal from './Modal';
import api, { friendlyApiError } from '../utils/api';
import { getStoredReferralCode } from '../utils/referralStorage';

const SignupModal = ({ onClose, onSwitchToLogin }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [referralPreview, setReferralPreview] = useState(null);
  const { signup } = useAuth();
  const { showNotification, dismissNotification } = useNotification();
  const turnstile = useTurnstile();

  useEffect(() => {
    const code = getStoredReferralCode();
    if (!code) return;
    setReferralPreview({ code, referrerUsername: null });
    api
      .get(`/referrals/validate/${encodeURIComponent(code)}`)
      .then(({ data }) => {
        if (data?.valid) {
          setReferralPreview({ code, referrerUsername: data.referrerUsername });
        }
      })
      .catch(() => {});
  }, []);

  const authBlocked = turnstile.enabled === true && !turnstile.verified;
  const dividerBg = 'bg-white dark:bg-gray-800';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!turnstile.assertVerified(showNotification)) return;
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    const loadingToastId = showNotification('Signing up...', 'loading', 0);

    try {
      await signup(email, password, username, { turnstileToken: turnstile.token });
      dismissNotification(loadingToastId);
      showNotification('Sign up successful!', 'success');
      onClose();
    } catch (err) {
      const message = friendlyApiError(err, 'Signup failed. Please try again.');
      setError(message);
      showNotification(message, 'error');
      turnstile.resetWidget();
    } finally {
      dismissNotification(loadingToastId);
      setLoading(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Sign Up" size="sm">
      <div className="relative">
        {loading && (
          <div
            className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-[1px]"
            aria-live="polite"
            aria-busy="true"
          >
            <svg className="w-10 h-10 text-blue-500 animate-spin mb-3" fill="none" viewBox="0 0 24 24" aria-hidden>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Signing up...</p>
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900 dark:text-red-200">
            {error}
          </div>
        )}

        {referralPreview?.code && (
          <div className="mb-4 rounded-lg border border-violet-200 bg-violet-50 p-3 text-sm text-violet-900 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-100">
            Referred by{' '}
            <strong>{referralPreview.referrerUsername || 'a friend'}</strong>
            <span className="ml-1 font-mono text-xs opacity-80">({referralPreview.code})</span>
            <p className="text-xs mt-1.5 opacity-80 leading-relaxed">
              After you make your first free prediction, they earn a referral reward.
            </p>
          </div>
        )}

        <div className={authBlocked ? 'opacity-50 pointer-events-none select-none' : ''}>
          <GoogleSignInButton onSuccess={onClose} onError={setError} />
        </div>

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300 dark:border-gray-600" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className={`px-2 text-gray-500 ${dividerBg}`}>Or sign up with email</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              placeholder="Choose a username"
              className="w-full rounded-lg border border-gray-300 px-4 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
              autoComplete="email"
              className="w-full rounded-lg border border-gray-300 px-4 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Min 8 characters"
              autoComplete="new-password"
              className="w-full rounded-lg border border-gray-300 px-4 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Confirm Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Confirm your password"
              autoComplete="new-password"
              className="w-full rounded-lg border border-gray-300 px-4 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
            />
          </div>

          <AuthTurnstileSection
            enabled={turnstile.enabled}
            loading={turnstile.loading}
            siteKey={turnstile.siteKey}
            resetKey={turnstile.resetKey}
            onVerify={turnstile.setToken}
            onExpire={turnstile.clearToken}
          />

          <button
            type="submit"
            disabled={loading || authBlocked}
            className="w-full rounded-lg bg-blue-500 py-2.5 text-white transition-colors hover:bg-blue-600 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden>
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Signing up...
              </>
            ) : authBlocked ? (
              'Complete security check to sign up'
            ) : (
              'Sign Up'
            )}
          </button>
        </form>

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300 dark:border-gray-600" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className={`px-2 text-gray-500 ${dividerBg}`}>Or</span>
          </div>
        </div>

        <div className={authBlocked ? 'opacity-50 pointer-events-none select-none' : ''}>
          <WalletConnectButton
            onSuccess={onClose}
            onConnectClick={onClose}
            beforeConnect={() => turnstile.assertVerified(showNotification)}
          />
        </div>

        <p className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
          Already have an account?{' '}
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="font-medium text-blue-500 hover:text-blue-600"
          >
            Login
          </button>
        </p>
      </div>
    </Modal>
  );
};

export default SignupModal;
